/**
 * Per-Facet Submit Buttons for Range Filters
 *
 * Prevents automatic form submission for price and size range filters,
 * enabling manual submission via "Apply" buttons rendered in Liquid.
 *
 * Persists across DOM updates using MutationObserver and theme:loading:end events.
 */

(function () {
  "use strict";

  const initializedForms = new WeakSet();
  const inputsWithListeners = new WeakSet();
  const buttonsWithHandlers = new WeakSet();
  let observerInitialized = false;

  function stopPropagation(e) {
    e.stopPropagation();
  }

  function makeInputsNonPropagating(form) {
    // Price range inputs
    form
      .querySelectorAll('price-range input[name^="filter.v.price"]')
      .forEach((input) => {
        if (inputsWithListeners.has(input)) {
          return;
        }
        input.addEventListener("change", stopPropagation);
        inputsWithListeners.add(input);
      });

    // Size range inputs: collect all inputs from size-range elements
    form.querySelectorAll("size-range").forEach((sizeRange) => {
      const allInputs = [
        ...sizeRange.querySelectorAll('input[type="range"]'),
        ...sizeRange.querySelectorAll('.input-group input[type="number"]'),
      ];

      allInputs.forEach((input) => {
        if (inputsWithListeners.has(input)) {
          return;
        }

        if (input.type === "range") {
          input.addEventListener("input", stopPropagation);
          input.addEventListener("change", stopPropagation);
        } else {
          input.addEventListener("change", stopPropagation);
        }

        inputsWithListeners.add(input);
      });
    });
  }

  function buildFacetUrl(facetsForm, filterContainer) {
    const url = new URL(window.location.href);
    const sectionId = facetsForm.getAttribute("section-id");

    if (filterContainer.matches("price-range")) {
      const priceInputs = filterContainer.querySelectorAll(
        'input[name^="filter.v.price"]'
      );

      priceInputs.forEach((input) => {
        if (!input.name) {
          return;
        }
        url.searchParams.delete(input.name);
        if (input.value) {
          url.searchParams.set(input.name, input.value);
        }
      });
    } else if (filterContainer.matches("size-range")) {
      const checkboxes = filterContainer.querySelectorAll(
        'input[type="checkbox"].size-checkbox'
      );
      if (checkboxes.length === 0) {
        sectionId && url.searchParams.set("section_id", sectionId);
        return url;
      }

      const baseParamName = checkboxes[0].name;
      const minRange = filterContainer.querySelector(
        'input[type="range"]:first-child'
      );
      const maxRange = filterContainer.querySelector(
        'input[type="range"]:last-child'
      );

      if (minRange && maxRange) {
        const minSize = parseInt(minRange.value);
        const maxSize = parseInt(maxRange.value);

        checkboxes.forEach((checkbox) => {
          const sizeValue = parseInt(checkbox.getAttribute("data-size-value"));
          checkbox.checked = sizeValue >= minSize && sizeValue <= maxSize;
        });
      }

      url.searchParams.delete(baseParamName);

      checkboxes.forEach((checkbox) => {
        if (checkbox.checked) {
          url.searchParams.append(baseParamName, checkbox.value);
        }
      });
    }

    sectionId && url.searchParams.set("section_id", sectionId);

    return url;
  }

  function attachButtonHandlers(facetsForm) {
    const buttons = facetsForm.querySelectorAll(".facet-apply-btn");
    buttons.forEach((button) => {
      if (buttonsWithHandlers.has(button)) {
        return;
      }
      buttonsWithHandlers.add(button);

      button.addEventListener("click", (e) => {
        e.preventDefault();

        const filterContainer = button.closest("price-range, size-range");
        if (!filterContainer) {
          return;
        }

        const url = buildFacetUrl(facetsForm, filterContainer);

        facetsForm.dispatchEvent(
          new CustomEvent("facet:update", {
            bubbles: true,
            detail: { url },
          })
        );
      });
    });
  }

  function initializeFacetsForm(facetsForm) {
    if (initializedForms.has(facetsForm)) {
      return;
    }

    const form = facetsForm.querySelector("form");
    if (!form) {
      return;
    }

    makeInputsNonPropagating(form);
    attachButtonHandlers(facetsForm);
    initializedForms.add(facetsForm);
  }

  function initializeAll() {
    const facetsForms = document.querySelectorAll("facets-form");
    facetsForms.forEach(initializeFacetsForm);

    if (!observerInitialized) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      observerInitialized = true;
    }
  }

  /**
   * Double requestAnimationFrame pattern: ensures DOM mutations are fully processed.
   *
   * Flow: When facet:update occurs, theme.js:
   * 1. Fetches new HTML content
   * 2. Dispatches theme:loading:end (after parsing, but BEFORE DOM update)
   * 3. Calls replaceChildren() to update the DOM
   *
   * Since theme:loading:end fires before the DOM is actually updated, we can't
   * initialize immediately. Double RAF ensures:
   * - First RAF: waits for current paint cycle to complete
   * - Second RAF: waits for next paint cycle, guaranteeing DOM mutations are
   *   processed, elements are rendered, and queryable before initialization.
   *
   * More reliable than setTimeout as it's synchronized with browser rendering.
   */
  function scheduleInitialization() {
    requestAnimationFrame(() => {
      requestAnimationFrame(initializeAll);
    });
  }

  const observer = new MutationObserver((mutations) => {
    const hasFacetsForm = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node instanceof Element &&
          (node.matches("facets-form") || node.querySelector("facets-form"))
      )
    );
    if (hasFacetsForm) {
      scheduleInitialization();
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeAll);
  } else {
    initializeAll();
  }

  document.addEventListener("theme:loading:end", scheduleInitialization);
})();
